import React, { useMemo, useCallback, useState, useEffect } from 'react'
import injectStyles from 'react-jss'
import { useRouteMatch } from 'react-router-dom'

import Sync from 'Containers/Sync'
import { SidebarWrapper } from 'Components/Sidebar/SidebarWrapper'
import GlobalSearch from '../../Components/GlobalSearch/container/GlobalSearch'
import UnreadIndicator from '../../Components/ChatUnreadIndicator/container/UnreadIndicator'

import { PATHS } from 'Config/Routing/paths'
import { generateSuggestionURL } from '../../Utils/GlobalSearch/GlobalSearchUtils'
import { isSmallScreen } from 'Utils/Misc'
import { authorization } from 'Module/Auth/Auth'
import { apiClient } from '../../Module/API/Client'

import ClientsIcon from 'Assets/Images/icons/clients'
import CitiesIcon from 'Assets/Images/icons/cities'
import CampaignsIcon from 'Assets/Images/icons/campaigns'
import UserManagementIcon from 'Assets/Images/icons/userManagement'
import SettingsIcon from 'Assets/Images/icons/settings'
import LogoutIcon from 'Assets/Images/icons/logout'
import ChatIcon from 'Assets/Images/icons/chat'

import SidebarButton from './components/SidebarButton'
import If from './components/If'
import IfElse from './components/IfElse'

import styles from './styles'

const isSidebarOpen = (() => {
  if (!localStorage.getItem('sidebarState')) return !isSmallScreen()
  return localStorage.getItem('sidebarState') === 'open'
})()

const Sidebar = ({ children, classes }) => {
  const [isOpen, setIsOpen] = useState(isSidebarOpen)
  const { path } = useRouteMatch()

  useEffect(() => {
    localStorage.setItem('sidebarState', isOpen ? 'open' : 'close')
  }, [isOpen])

  const {
    isShowGlobalSearch,
    isShowClients,
    isShowCampaigns,
    isShowCities,
    isShowUserManagement,
    isShowSettings,
    isShowChat,
  } = useMemo(() => {
    const isSpy = authorization.isSpy()
    const isAdmin = authorization.isAdmin()
    const isCityManager = authorization.isCityManager()
    const isClient = authorization.isClient()
    const isClientAdmin = authorization.isClientAdmin()

    return {
      isShowGlobalSearch: !isSpy && (isAdmin || isCityManager),
      isShowClients: !isSpy && !isClient,
      isShowCampaigns: !isSpy && (isClient || isCityManager || isAdmin),
      isShowCities: isCityManager || isAdmin || isSpy,
      isShowUserManagement: isAdmin || isClientAdmin,
      isShowSettings: isAdmin || isCityManager,
      isShowChat: !isSpy && (isCityManager || isAdmin),
    }
  }, [])

  const onToggle = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  const searchRequestHandler = useCallback(async searchedValue => {
    const response = await apiClient.globalSearch(searchedValue)
    return response.json()
  }, [])

  return (
    <SidebarWrapper content={children} onToggle={onToggle} open={isOpen}>
      <IfElse condition={isShowGlobalSearch}>
        <GlobalSearch
          onSuggestionSelected={suggestion => {
            window.location.href = generateSuggestionURL(suggestion)
          }}
          searchRequestHandler={searchRequestHandler}
          showButton={true}
        />
        <span className={classes.border} />
      </IfElse>
      <If condition={isShowClients}>
        <SidebarButton
          id="clients-button"
          icon={<ClientsIcon />}
          width={'22px'}
          to={`${path}${PATHS.dashboard.clients}`}
        >
          Clients
        </SidebarButton>
      </If>
      <If condition={isShowCampaigns}>
        <SidebarButton
          id="campaigns-button"
          icon={<CampaignsIcon />}
          width={'16px'}
          to={`${path}${PATHS.dashboard.campaigns}?Status=Active`}
          isActive={(match, location) => {
            return location.pathname.startsWith(
              path + PATHS.dashboard.campaigns,
            )
          }}
        >
          Campaigns
        </SidebarButton>
      </If>
      <If condition={isShowCities}>
        <SidebarButton
          id="cities-button"
          icon={<CitiesIcon />}
          width={'18px'}
          to={`${path}${PATHS.dashboard.cities}`}
        >
          Cities
        </SidebarButton>
      </If>
      <If condition={isShowUserManagement}>
        <SidebarButton
          icon={<UserManagementIcon />}
          width={'16px'}
          to={`${path}${PATHS.dashboard.userManagement}`}
        >
          User Management
        </SidebarButton>
      </If>
      <If condition={isShowSettings}>
        <SidebarButton
          icon={<SettingsIcon />}
          width={'16px'}
          to={`${path}${PATHS.dashboard.settings}`}
        >
          Settings
        </SidebarButton>
      </If>

      <If condition={isShowChat}>
        <SidebarButton
          icon={<ChatIcon />}
          width={'22px'}
          to={`${path}${PATHS.dashboard.chat}`}
        >
          <div style={{ display: 'flex', alignItems: 'center', float: 'left' }}>
            Chat
            <UnreadIndicator />
          </div>
        </SidebarButton>
      </If>
      <SidebarButton
        icon={<LogoutIcon />}
        width={'18px'}
        to={PATHS.main.logout}
      >
        Logout
      </SidebarButton>
      <Sync className={classes.sync} />
    </SidebarWrapper>
  )
}

export default injectStyles(styles)(Sidebar)



// WEBPACK FOOTER //
// ./src/Containers/Sidebar/index.js